// @flow
import { actionTypes } from '~/core/constants/actions/';
import { alerts, feedback } from '~/core/constants';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { dashboardAddProduct, saveDashboardEdit, receiveDashboardCustomisation, saveDashboardSuccess } from './actions';
import { GET_DASHBOARD_CUSTOMISATION, SAVE_DASHBOARD_EDIT } from './constants';
import { getConfig } from '~/core/state/config/selectors';
import { getDashboardCustomisation, saveDashboardCustomisation } from '~/core/api/dashboard';
import { customisationErrors } from './intl';
import { END_ATTEMPT_CREATE_ACTION } from '~/core/state/actions/constants';
import { fromJS } from 'immutable';
import { getActionsList, isQuickAction } from '~/core/state/actions/selectors';
import { getCurrentTime } from '~/core/state/hub/selectors';
import { getDashboardGroups, getIdsToBeDeleted } from './selectors';
import { getProductItems } from '~/core/state/products/selectors';
import { getUserId } from '~/core/state/user/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { noop } from 'lodash/fp';
import { requestAlert } from '~/core/state/alerts/actions';
import { attemptRemoveAction } from '~/core/state/actions/actions';

/***********************************
 *   GET DASHBOARD CUSTOMISATION   *
 ***********************************/

export function* getDashboardCustomisationSaga(): Generator<Effect | noop, *, *> {
    const userId = yield select(getUserId);
    const products = yield select(getProductItems);
    const actions = yield select(getActionsList);
    const [customisation, error] = yield call(handleApi(getDashboardCustomisation), userId);

    if (!error) {
        return yield put(receiveDashboardCustomisation({ actions, customisation, products }));
    }

    yield put(receiveDashboardCustomisation({ actions, customisation: { pages: [] }, products }));

    if (!error.status === 404) {
        yield put(requestAlert(customisationErrors.fetchError, alerts.types.ERROR));
    }
}

function* watchDashboardCustomisationSaga(): Generator<Effect, *, *> {
    yield takeLatest(GET_DASHBOARD_CUSTOMISATION, handleSaga(getDashboardCustomisationSaga));
}

export function* isDashboardCustomised(): Generator<Effect | noop, *, *> {
    const userId = yield select(getUserId);
    const isDashboardCustomisationDataOn = yield select(getConfig, 'features:dashboardCustomisationData');
    if (!isDashboardCustomisationDataOn) {
        return false;
    }

    const [{ pages }, error] = yield call(handleApi(getDashboardCustomisation), userId);

    if (error) {
        return yield put(requestAlert(customisationErrors.fetchError, alerts.types.ERROR));
    }

    return pages && pages.length;
}

export function* saveDashboardCustomisationSaga(): Generator<Effect | noop, *, *> {
    const userId = yield select(getUserId);
    const hubNow = yield select(getCurrentTime);
    const pages = yield select(getDashboardGroups);

    if (!pages) {
        return;
    }

    const sortedPages = pages.filter(p => p.get('items').count() > 0).map((p, index) => p.set('position', index));

    //Filter out products and actions that don't exist
    const products = yield select(getProductItems);
    const actions = yield select(getActionsList);
    const customisationPages = sortedPages.map(cp =>
        cp.set(
            'items',
            cp
                .get('items')
                .filter(
                    item =>
                        products.some(p => p.get('id') === item.get('id')) || actions.some(a => a.id === item.get('id'))
                )
        )
    );

    const [, error] = yield call(handleApi(saveDashboardCustomisation), userId, hubNow, customisationPages.toJS());

    if (!error) {
        // Remove from actions if product is Quick-Action
        const removedIds = yield select(getIdsToBeDeleted);

        for (const id of removedIds) {
            const alsoDeleteAction = yield select(isQuickAction, { actionId: id });
            if (alsoDeleteAction) {
                yield put(attemptRemoveAction(id));
            }
        }
    }

    yield put(saveDashboardSuccess(error || fromJS({ pages: customisationPages, lastUpdated: hubNow })));

    if (error) {
        yield put(requestAlert(feedback.generic.SAVE_FAILED, alerts.types.ERROR));
    }
}

function* watchSaveDashboardCustomisationSaga(): Generator<Effect, *, *> {
    yield takeLatest(SAVE_DASHBOARD_EDIT, handleSaga(saveDashboardCustomisationSaga));
}

function* addActionToDashboardSaga({ payload: hiveAction }): Generator<Effect | noop, *, *> {
    const quickAction = hiveAction.events.find(a => a.group === 'when' && a.type === actionTypes.QUICK_ACTION);

    if (quickAction) {
        yield put(dashboardAddProduct(hiveAction.id));
        yield put(saveDashboardEdit());
    }
}

function* watchEndCreateAction(): Generator<Effect, *, *> {
    yield takeLatest(END_ATTEMPT_CREATE_ACTION, handleSaga(addActionToDashboardSaga));
}

/*************************/

export default [watchDashboardCustomisationSaga, watchEndCreateAction, watchSaveDashboardCustomisationSaga];



// WEBPACK FOOTER //
// ./src/core/state/dashboard/sagas.js